import axios from "axios";
import { useState } from "react";
import { getCardFromPerson, omit } from "../utils";

export const useContextData = () => {
	// Axios Instance Configurations
	const axiosInstance = axios.create({
		baseURL: process.env.REACT_APP_BACKEND_URL,
		headers: {
			"x-auth-token": localStorage.getItem("token"),
			"Content-Type": "application/json",
		},
	});

	// Loading State
	const [isLoading, setIsLoading] = useState(false);

	// Global Authentication State
	const isLocalAuthenticated = localStorage.getItem("isAuthenticated");
	const [isAuthenticated, setIsAuthenticated] = useState(
		JSON.parse(isLocalAuthenticated) || false
	);

	// Global User State
	const [user, setUser] = useState(
		JSON.parse(localStorage.getItem("user")) || null
	);
	const [token, setToken] = useState(localStorage.getItem("token") || null);
	const updateUser = (newUser) => {
		localStorage.removeItem("user");
		setUser(null);
		localStorage.setItem(
			"user",
			JSON.stringify(omit({ ...user, ...newUser }, "password"))
		);
		setUser((p) => ({ ...p, ...newUser }));
	};
	const verifyUser = async () => {
		try {
			setIsLoading(true);
			const res = await axiosInstance.get("/api/auth");
			setUser(res.data.user);
			localStorage.setItem("user", JSON.stringify(res.data.user));
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			localStorage.removeItem("token");
			localStorage.removeItem("user");
			localStorage.setItem("isAuthenticated", false);
			setUser(null);
			setIsAuthenticated(false); // If the user couldn't be verified, logout the user
		}
	};

	// Global Team State
	const [people, setPeople] = useState([]);
	const [team, setTeam] = useState([]);
	const getAllPeople = async () => {
		try {
			setIsLoading(true);
			const res = await axiosInstance.get("/api/team");
			setPeople(() => res.data);
			setIsLoading(false);
			return res.data;
		} catch (error) {
			alert(error?.response?.data?.message);
			setIsLoading(false);
		}
	};
	const restructureTeam = (people) => {
		let parseTeam = [];
		people.forEach((person) => {
			let obj = getCardFromPerson(person);
			parseTeam.push(obj);
		});
		let cooridnators = [];
		let executives = [];
		let mentors = [];
		let office = [];

		parseTeam.forEach((member) => {
			let mail = member.roll + "@iiitu.ac.in";
			if (mail.slice(0, 2) === "19") mentors = [...mentors, member];
			else if (mail.slice(0, 2) === "20")
				cooridnators = [...cooridnators, member];
			else if (mail.slice(0, 2) === "21")
				executives = [...executives, member];
			if (
				mail === "19331@iiitu.ac.in" ||
				mail === "19137@iiitu.ac.in" ||
				mail === "19307@iiitu.ac.in"
			)
				office = [...office, member];
		});
		let finalTeam = [
			{
				title: "Office Bearers",
				members: office,
			},
			{
				title: "Mentors",
				members: mentors,
			},
			{
				title: "Coordinators",
				members: cooridnators,
			},
			{
				title: "Executive Members",
				members: executives,
			},
		];
		setTeam(() => finalTeam);
	};
	const addNewMember = async (newMember) => {
		try {
			setIsLoading(true);
			await axiosInstance.post("/api/users/create", newMember);
			getAllPeople();
			setIsLoading(false);
			return true;
		} catch (error) {
			setIsLoading(false);
			alert(error?.response?.data?.message);
			return false;
		}
	};
	const updateMember = async (updatedMember) => {
		try {
			setIsLoading(true);
			const res = await axiosInstance.put(
				`/api/users/update/${updatedMember.roll}`,
				updatedMember
			);
			await getAllPeople();
			setIsLoading(false);
			return res.data;
		} catch (error) {
			setIsLoading(false);
			alert(error?.response?.data?.message);
			return error.response.data;
		}
	};
	const removeMember = async (roll) => {
		try {
			setIsLoading(true);
			const res = await axiosInstance.post("/api/users/remove", {
				roll: roll,
			});
			await getAllPeople();
			setIsLoading(false);
			return res.data;
		} catch (error) {
			setIsLoading(false);
			alert(error?.response?.data?.message);
			return error.response.data;
		}
	};

	// Global Applications Status
	const [applications, setApplications] = useState([]);
	const getAllApplications = async () => {
		try {
			setIsLoading(true);
			const res = await axiosInstance.get("/api/applications");
			setApplications(() => res.data);
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			alert(error?.response?.data?.message);
		}
	};
	const createNewApplication = async (newApplication) => {
		try {
			setIsLoading(true);
			await axiosInstance.post("/api/application", newApplication);
			await getAllApplications();
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			alert(error?.response?.data?.message);
		}
	};
	const updateApplication = async (id, updatedApplication) => {
		try {
			setIsLoading(true);
			await axiosInstance.put(
				`/api/application/${id}`,
				updatedApplication
			);
			await getAllApplications();
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			alert(error?.response?.data?.message);
		}
	};
	const deleteApplication = async (id) => {
		try {
			setIsLoading(true);
			await axiosInstance.delete(`/api/application/${id}`);
			await getAllApplications();
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			alert(error?.response?.data?.message);
		}
	};

	// Media Breakpoints
	const mediaQuerySm = window.matchMedia("(max-width: 672px)");
	const mediaQueryMd = window.matchMedia("(max-width: 880px)");
	const mediaQueryLg = window.matchMedia("(min-width: 880px)");
	const breakpoint = (device) => {
		if (device === "mobile") return mediaQuerySm.matches;
		else if (device === "tab") return mediaQueryMd.matches;
		else return mediaQueryLg.matches;
	};
	mediaQuerySm.addListener(breakpoint);
	mediaQueryMd.addListener(breakpoint);
	mediaQueryLg.addListener(breakpoint);

	return {
		breakpoint,
		isLoading,
		setIsLoading,
		isAuthenticated,
		setIsAuthenticated,
		user,
		setUser,
		token,
		setToken,
		updateUser,
		verifyUser,
		axiosInstance,
		applications,
		setApplications,
		getAllApplications,
		createNewApplication,
		updateApplication,
		deleteApplication,
		people,
		setPeople,
		getAllPeople,
		addNewMember,
		updateMember,
		removeMember,
		team,
		setTeam,
		restructureTeam,
	};
};
